// Yellow Agency, Case Studies data
// Ported from internalcommsagency.co.uk case studies (same parent company).
// Body copy retained; brand references updated to Yellow where applicable.

export const CASE_STUDY_CATEGORIES = [
  { key: 'all', label: 'All Work' },
  { key: 'public-awareness', label: 'Public Awareness' },
  { key: 'staff-engagement', label: 'Staff Engagement' },
  { key: 'education', label: 'Education' },
  { key: 'training', label: 'Training' },
  { key: 'recruitment', label: 'Recruitment' }
];

export const CASE_STUDIES = [
  {
    slug: 'the-brink-forward-trust-recovery',
    title: 'Telling Stories of Recovery: The Brink & Forward Trust',
    client: 'Forward Trust, The Brink',
    sector: 'Charity & Addiction Recovery',
    category: 'public-awareness',
    categoryLabel: 'Public Awareness',
    summary: "We partnered with Forward Trust and The Brink, the UK's first ever sober bar, on a public awareness campaign that told the real stories of people whose lives were saved and rebuilt through the recovery support offered right in the heart of Liverpool.",
    hero: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/gwssnh2i_IMG_6290.webp',
    challenge: "The Brink in Liverpool is a not-for-profit sober bar delivered by Forward Trust, offering support for anyone facing drug, alcohol or gambling addiction.\n\nBut stigma, isolation and shame stop people asking for help. Forward Trust needed a campaign that could break through and show real help exists on Merseyside's doorstep.",
    solution: "We built the campaign around real people, not scripted messages.\n\nWe filmed at The Brink and interviewed staff and regulars with lived experience of recovery, then produced a supporting photography library the charity can keep using across every channel.",
    results: [
      'A recovery-led awareness campaign built entirely around real Merseyside stories',
      "Deeper awareness of The Brink as a route into Forward Trust's wider support services, not just a café",
      'Content that inspired people to reach out, showing help is available on their doorstep',
      "Reinforced The Brink's reputation as a 'beacon of hope' in the Liverpool City Region"
    ],
    services: ['Video Production', 'Documentary-style Filming', 'Interview Direction', 'Campaign Photography', 'Social Media Content', 'Post-production'],
    videos: [
      { type: 'youtube', id: 'GZPUt9JoLWg' },
      { type: 'youtube', id: 'hWtw3BlW_qY' }
    ],
    gallery: [
      'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/gwssnh2i_IMG_6290.webp',
      'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/bevoidbw_IMG_6055.webp',
      'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/c032vuqy_IMG_6033.webp',
      'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/zaep6h28_IMG_5668.webp'
    ]
  },
  {
    slug: 'sheffield-autism-friendly-festival',
    title: "Raising Awareness for Sheffield's First Autism Friendly Festival",
    client: 'Sheffield City Council',
    sector: 'Local Government & Social Care',
    category: 'public-awareness',
    categoryLabel: 'Public Awareness',
    summary: "We partnered with Sheffield City Council to launch a full public awareness campaign for the city's first Autism Friendly Festival, showcasing the support and services available to autistic people and their families across Sheffield, and driving attendance to a week of council-funded events.",
    hero: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/3p4n38b3_image.png',
    challenge: "As part of World Autism Acceptance Month 2025, Sheffield City Council wanted to launch the city's first Autism Friendly Festival: a three-day programme of talks, exhibitions, experience buses and drop-in sessions delivered with Sheffield Hallam University.\n\nFor it to succeed the campaign had to reach autistic people, families, carers and educators fast, and show the council-funded support network already available across the city.",
    solution: "We delivered an end-to-end awareness campaign built around brand identity, film, a dedicated website and print collateral.\n\nA warm multi-colour identity anchored on the autism infinity symbol rolled out across banners and merchandise. A festival microsite housed every event, and four short films captured the range of activities and the people behind them.",
    results: [
      'The city\'s first Autism Friendly Festival delivered as a huge success across three days of events',
      'Full awareness campaign reaching autistic people, families, carers, employers and educators across Sheffield',
      "Dedicated festival microsite acting as the single source of truth for every event and support service",
      "Strengthened public perception that Sheffield cares, and actively supports, its autistic community"
    ],
    services: ['Brand Identity', 'Website Design & Development', 'Video Production', 'Print & Merchandise Design', 'Campaign Strategy', 'Social Media Content'],
    videos: [
      { type: 'youtube', id: 'fw2flmnwwI4' },
      { type: 'youtube', id: 'SzyANUoUJuE' },
      { type: 'youtube', id: 'XSwaii80ahE' },
      { type: 'youtube', id: '8lbnR931hMU' }
    ],
    sections: [
      {
        heading: 'Website Design',
        body: "We designed and built the dedicated Sheffield Autism Festival microsite, a single place for autistic people, families and carers to browse every event, learn about the supporting organisations, and book their place. Built with autism-friendly design principles in mind: calm palette, clear language, generous spacing and simple navigation.",
        images: [
          'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/9zutqpc4_sheffield%20autism%20website.webp'
        ]
      },
      {
        heading: 'Branding & Design',
        body: "A full brand identity built around the autism infinity symbol in a soft multi-colour gradient, rolled out across street banners, tote bags, keyrings, badges and event signage to give the festival a warm, unified presence across the city.",
        images: [
          'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/3p4n38b3_image.png',
          'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/gwf6qveu_image.png'
        ]
      }
    ]
  },
  {
    slug: 'liverpool-john-moores-university-schools-outreach',
    title: "Growing Awareness of LJMU's Schools Outreach Programme",
    client: 'Liverpool John Moores University',
    sector: 'Higher Education',
    category: 'public-awareness',
    categoryLabel: 'Public Awareness',
    summary: "We helped Liverpool John Moores University grow awareness of the outreach they deliver to local schools, encouraging more young people to consider higher education, and showcasing the growing partnerships the university has built across the Liverpool community.",
    hero: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/3ohwwwwt_image.png',
    challenge: "Liverpool John Moores runs an extensive schools outreach programme that opens up conversations about higher education for young people who might not otherwise consider it.\n\nBut the scale of that work wasn't always visible beyond the schools involved. LJMU needed content that raised awareness of the partnerships and reinforced the university's core values across the community.",
    solution: "We produced three films built around LJMU's values (Community, Inclusive and Courageous), each supported by a matching set of print and digital adverts.\n\nThe films captured genuine moments from schools across Liverpool, and the advertising extended the message across print, digital and out of home channels.",
    results: [
      "Raised awareness of LJMU's schools outreach programme across the Liverpool region",
      'Strengthened relationships with existing school partners and opened doors to new ones',
      'Values-led campaign aligning every message with Community, Inclusive and Courageous',
      'Content used by the internal comms and marketing teams across multiple campaigns and events'
    ],
    services: ['Video Production', 'Advertising Design', 'Campaign Photography', 'Brand Storytelling', 'On-location Filming', 'Post-production'],
    videos: [
      { type: 'youtube', id: 'SwWy4d2lYaM' },
      { type: 'youtube', id: '_Z2PJxr8rNw' },
      { type: 'youtube', id: 'VHT5NqIYsuE' }
    ],
    sections: [
      {
        heading: 'Advertising',
        body: "A series of print and digital adverts built around LJMU's core values, Community, Inclusive and Courageous, designed to run across the schools outreach campaign.",
        images: [
          'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/wqfahwu0_Community.jpg',
          'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/hnhh5jpf_INCLUSIVE_.jpg',
          'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/1ax665fy_Courageos.jpg'
        ]
      }
    ]
  },
  {
    slug: 'brew-it-group-exhibition-experience-centre',
    title: 'Bringing Speciality Coffee Equipment to Life for Brew-It Group',
    client: 'Brew-It Group',
    sector: 'Speciality Coffee Equipment',
    category: 'public-awareness',
    categoryLabel: 'Public Awareness',
    summary: "We partnered with Brew-It Group to bring their exhibition presence, product range, manufacturer partnerships and Experience Centre to life, capturing hands-on demonstrations, training sessions and brand new equipment in use, and turning it all into a full library of marketing assets.",
    hero: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/895e5gqs_IMG_8740.webp',
    challenge: "Brew-It Group supplies speciality coffee equipment from names like Ceado, La Spaziale and Mahlkönig, but many of those manufacturers didn't have up to date footage of new products.\n\nOn top of that, Brew-It wanted to raise the profile of their Experience Centre: the hands-on training space that sets them apart from bigger, less consultative suppliers.",
    solution: "We built a rolling content programme covering three areas: exhibition coverage, fresh product content filmed at the Experience Centre, and event content around the training space.\n\nWe also designed and built the Brew-It Group website, using the new photography and video throughout to bring each brand story to life.",
    results: [
      'Fresh product content bringing manufacturer partners bang up to date',
      "Stronger positioning of Brew-It's Experience Centre as a differentiator against competitors",
      'Consistent, high-quality content supporting event marketing and customer training sessions',
      'A modern, professional visual identity across the Brew-It Group digital presence'
    ],
    services: ['Website Design & Development', 'Video Production', 'Product Photography', 'Event & Exhibition Coverage', 'Training Content', 'Post-production', 'Social Media Assets'],
    videos: [
      { type: 'youtube', id: 'q7vlB6LFUyU' },
      { type: 'youtube', id: 'AvAMkFDcK2M' }
    ],
    sections: [
      {
        heading: 'Website Design',
        body: "We designed and built the Brew-It Group website from the ground up, showcasing their manufacturer partnerships, product range and Experience Centre, with the photography and video content we produced woven throughout to bring each brand story to life.",
        images: [
          'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/v536u2hk_image.png'
        ]
      },
      {
        heading: 'Design',
        body: "Brand-led print brochures and email campaigns designed to bring each manufacturer partner to life, from La Spaziale and La San Marco espresso machines to Puqpress tampers and the latest launches from Ceado and Mahlkönig.",
        stacked: true,
        images: [
          'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/jyd7l1gm_Brochure%20Mock.webp',
          'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/48lzw5pu_Brochure%20Mock%202.webp',
          'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/0shqptql_Email%20mocks.webp'
        ]
      }
    ],
    gallery: [
      'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/ddr3zpra_IMG_6078.webp',
      'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/h46t12jr_IMG_6257.webp',
      'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/895e5gqs_IMG_8740.webp',
      'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/ooui44ab_IMG_4058_800x533.jpg'
    ]
  },
  {
    slug: 'liverpool-hope-university-student-stories',
    title: "Say Yes: Bringing Liverpool Hope University's Student Stories to Life",
    client: 'Liverpool Hope University',
    sector: 'Higher Education',
    category: 'education',
    categoryLabel: 'Education',
    summary: "We partnered with Liverpool Hope University to help them stand out from the city's bigger institutions, through a series of authentic student story films, supporting photography and a dedicated apprenticeships campaign that showed prospective students exactly what life at Hope really feels like.",
    hero: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/6eo1vyeh_IMG_9252.webp',
    challenge: "With several universities in Liverpool, bigger names often dominate the conversation and Liverpool Hope's story wasn't always cutting through, despite world-class facilities and outstanding student outcomes.\n\nHope also wanted to raise awareness of a lesser-known part of their offer: degree-level apprenticeships, which needed their own dedicated push.",
    solution: "We produced a series of authentic student story films around the 'Say Yes to New Experiences' campaign, capturing real testimonials shot on location across the campus.\n\nAlongside the films we ran a full photography programme covering campus life, open days and student ambassadors, and delivered a dedicated apprenticeships campaign giving that pathway the visibility it deserved.",
    results: [
      'Dedicated apprenticeships campaign raising awareness of degree-level apprenticeship pathways',
      "Stronger differentiation from Liverpool's larger universities through authentic, story-led content",
      'Digital marketing assets contributing directly to increased application interest and open day attendance',
      'Authentic student story films landing across web, social and paid campaigns'
    ],
    services: ['Video Production', 'Student Story Films', 'Campaign Photography', 'Apprenticeship Campaign', 'Social Media Content', 'On-location Filming', 'Post-production'],
    videos: [
      { type: 'youtube', id: 'c_9NzJ1Ymps' },
      { type: 'youtube', id: 'f8jcMzb7h98' }
    ],
    sections: [
      {
        heading: 'Advertising',
        body: "The 'Say Yes to New Experiences' campaign extended out of home too, with Liverpool Hope's ambitious student stories appearing on the back of city buses across the region to drive open day sign-ups.",
        images: [
          'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/9j90z73d_hope%20bus.png'
        ]
      }
    ],
    gallery: [
      'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/wv2r5zo8_IMG_9263.webp',
      'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/s3urfbg3_IMG_2422.webp',
      'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/6eo1vyeh_IMG_9252.webp',
      'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/9fqpd345_IMG_8599.webp',
      'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/hfhdcs73_IMG_9181.webp',
      'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/kvrs49ca_IMG_9122.webp',
      'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/dlzun5yd_IMG_2988.webp',
      'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/kzzqssou_IMG_0305.webp'
    ]
  },
  {
    slug: 'medicash-heart-of-liverpool',
    title: 'Telling the Story of a Liverpool Institution: Medicash',
    client: 'Medicash',
    sector: 'Healthcare & Social Responsibility',
    category: 'public-awareness',
    categoryLabel: 'Public Awareness',
    summary: "We partnered with Medicash to bring their 150-year Liverpool story to life, showcasing how the health cash plan provider has shaped the city, championed local causes and continued to give back to the community that built it.",
    hero: 'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/af67vv26_IMG_0049.webp',
    challenge: "Medicash has been part of the fabric of Liverpool for over 150 years, but that story wasn't being told outside their walls.\n\nDespite roots in the Victorian 'penny in the pound' era and a critical role in saving St Paul's Eye Hospital, public awareness had faded. They needed to reintroduce themselves as a proudly Liverpool-built business, not a faceless national provider.",
    solution: "We produced a two-part video campaign blending Medicash's Victorian heritage with the human stories of the people who work there today.\n\nWe filmed on location across Liverpool and interviewed leadership and employees, giving Medicash a full library of assets to use across web, social, sales and community events.",
    results: [
      "Significant lift in local brand awareness, Medicash re-positioned as a proudly Liverpool-built organisation, not a faceless national provider",
      "Stronger community perception, with the films actively shared across Liverpool business networks and civic partners",
      "Employees given powerful content to share, boosting pride and internal engagement",
      "Proof-point storytelling that supports Medicash's ongoing charity and CSR partnerships"
    ],
    services: ['Video Production', 'Brand Storytelling', 'Script Development', 'On-location Filming', 'Interviews', 'Post-production', 'Social Media Content'],
    videos: [
      { type: 'youtube', id: 'znD81ZLQFXc' },
      { type: 'youtube', id: 'rDOVSTyqEKs' }
    ],
    gallery: [
      'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/af67vv26_IMG_0049.webp',
      'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/m4drh3sk_IMG_0031.webp',
      'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/u7vo4lwy_IMG_0024.webp',
      'https://customer-assets-m6fa6gv7.emergentagent.net/job_f12b922e-b1fd-440c-9793-474906f03870/artifacts/xc7yv1hk_IMG_0012.webp'
    ]
  },
  {
    slug: 'morgan-sindall-protecting-people',
    title: 'Protecting People: Transforming Health & Safety Training',
    client: 'Morgan Sindall',
    sector: 'Construction & Infrastructure',
    category: 'training',
    categoryLabel: 'Training',
    summary: "We produced two comprehensive health and safety training programmes for Morgan Sindall, featuring over 50 filmed scenes with real employees to transform workplace safety culture.",
    hero: 'https://customer-assets.emergentagent.com/job_people-comms-hub/artifacts/h30gr4yz_IMG_0060%20%282%29%20%281%29_800x533.jpg',
    challenge: "Morgan Sindall needed to overhaul their health and safety training with content that landed with everyone, from leadership making budget decisions to teams on the tools each day.\n\nOff-the-shelf training wasn't going to shift culture. They needed something authentic to their sites and their people.",
    solution: "Working with Gateways Hub Consultants, we produced two training series: 'Protecting People' and 'Protecting People 2.0', featuring over 50 filmed scenes of business-typical moments and their consequences.\n\nEvery role was performed by real Morgan Sindall employees rather than actors, giving the training a believability that scripted content can't match.",
    results: [
      'Successfully launched company-wide health and safety policy overhaul',
      'Commissioned for second series due to exceptional feedback',
      'Training now integral part of company ethos and policy changes',
      'Over 50 authentic scenes filmed with real employees'
    ],
    services: ['Video Production', 'Strategy Development', 'Script Writing', 'On-site Filming', 'Post-production'],
    videos: [
      { type: 'youtube', id: 'KCTVuhVJIQA' }
    ],
    gallery: [
      'https://customer-assets.emergentagent.com/job_people-comms-hub/artifacts/xktvmtqy_IMG_0074_800x533.jpg',
      'https://customer-assets.emergentagent.com/job_people-comms-hub/artifacts/dd4t76xr_IMG_0275%20%281%29_800x533.jpg',
      'https://customer-assets.emergentagent.com/job_people-comms-hub/artifacts/pg61114j_IMG_0372_800x533.jpg',
      'https://customer-assets.emergentagent.com/job_people-comms-hub/artifacts/q26v0qwj_IMG_2004_800x600.JPG',
      'https://customer-assets.emergentagent.com/job_people-comms-hub/artifacts/ca56rb4x_IMG_0396_800x533.jpg',
      'https://customer-assets.emergentagent.com/job_people-comms-hub/artifacts/ijag42dy_IMG_2166_800x600.JPG',
      'https://customer-assets.emergentagent.com/job_people-comms-hub/artifacts/msavxldg_IMG_2212_800x600.JPG'
    ]
  },
  {
    slug: 'sheffield-city-council-be-the-missing-piece',
    title: 'Be The Missing Piece: Recruitment Campaign',
    client: 'Sheffield City Council',
    sector: 'Local Government & Social Care',
    category: 'recruitment',
    categoryLabel: 'Recruitment',
    summary: "We planned and executed Sheffield City Council's 'Be The Missing Piece' recruitment campaign to encourage more young people to enter careers in Adult Health & Social Care, driving hundreds of potential carers to open days, career workshops and job applications.",
    hero: 'https://customer-assets.emergentagent.com/job_people-comms-hub/artifacts/w6z93j00_carers.jpg',
    challenge: "Sheffield City Council needed to address a critical shortage of social care workers, particularly attracting more young people into the sector.\n\nThe campaign had to make careers in Adult Health & Social Care feel rewarding and worth applying for.",
    solution: "We delivered a full-service campaign covering strategy, video, advertising design, social content and a dedicated campaign website.\n\nProfessional photography captured the diversity of Sheffield's carers, culminating in a major open day at Sheffield Town Hall where hundreds of candidates met agencies and learned about available roles.",
    results: [
      'Hundreds of potential carers driven to open days and workshops',
      'Successful recruitment fair at Sheffield Town Hall',
      'Increased applications into Adult Health & Social Care roles across Sheffield',
      'Stronger public awareness of caring as a rewarding, meaningful career path'
    ],
    services: ['Strategy Development', 'Video Production', 'Graphic Design', 'Website Development', 'Photography', 'Social Media Content', 'Advertising'],
    videos: [
      { type: 'youtube', id: 'lfU4Ulm21e0' },
      { type: 'youtube', id: 'VZoSUCLjShI' }
    ],
    sections: [
      {
        heading: 'Website for Campaign',
        images: ['https://customer-assets.emergentagent.com/job_people-comms-hub/artifacts/ki0uixkd_sheffield-website-mock-up.jpg']
      },
      {
        heading: 'Advertising Examples for Campaign',
        images: ['https://customer-assets.emergentagent.com/job_people-comms-hub/artifacts/3u13702b_sheffield-bar-1024x408.jpg']
      },
      {
        heading: 'Photography Examples from Campaign',
        images: [
          'https://customer-assets.emergentagent.com/job_3771b25c-e884-485c-96c9-69f715a917e7/artifacts/56l1l0tu_Sheffield%20stills_1.36.1.jpg',
          'https://customer-assets.emergentagent.com/job_people-comms-hub/artifacts/nn65ytud_IMG_4414_800x533.jpg',
          'https://customer-assets.emergentagent.com/job_people-comms-hub/artifacts/w3jtvp5p_IMG_5637_800x533.jpg',
          'https://customer-assets.emergentagent.com/job_people-comms-hub/artifacts/4z4mvp8m_IMG_6047_800x533.jpg',
          'https://customer-assets.emergentagent.com/job_people-comms-hub/artifacts/3ro2aw25_IMG_6233_800x533.jpg',
          'https://customer-assets.emergentagent.com/job_people-comms-hub/artifacts/b6pv6s5f_IMG_6691_800x533.jpg'
        ]
      },
      {
        heading: 'The Open Day Success',
        body: 'The photos below are from the amazingly successful open day for the campaign, which brought in hundreds of potential social workers to talk with agencies with vacancies. This is one of our proudest campaigns ever. Not just for its success but for the real tangible difference it made to the people of Sheffield.',
        images: ['https://customer-assets.emergentagent.com/job_people-comms-hub/artifacts/rofhsnbt_sheffield-open%20day.jpg']
      }
    ]
  },
  {
    slug: 'lambeth-healthcare-values-recruitment',
    title: 'Communicating Values & Attracting Doctors: A National Campaign',
    client: 'Lambeth Healthcare',
    sector: 'Healthcare / NHS',
    category: 'staff-engagement',
    categoryLabel: 'Staff Engagement',
    summary: "We partnered with Lambeth Healthcare to introduce their values and vision to GP practices across the federation, while simultaneously launching a national recruitment campaign to attract doctors to the Lambeth area of London, an area that had struggled to recruit. The multi-channel campaign included video production, a new website, digital advertising, graphic design and a week-long careers event.",
    hero: 'https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/q8txvzel_header%20image.JPG',
    challenge: "Lambeth Healthcare needed to communicate its values across its GP federation, while simultaneously attracting doctors nationally to a London borough that had struggled to recruit.\n\nBoth audiences needed a compelling case, not just a job listing.",
    solution: "We produced a brand film to strengthen engagement within the federation, built a new website, and ran a national digital advertising campaign targeting GPs across the UK.\n\nWe also helped organise and promote 'Lambeth Careers', a week-long online programme of events, supported by brochures, billboards and branded materials.",
    results: [
      'Successful national recruitment campaign attracting GP interest from across the UK',
      "Stronger brand engagement and values awareness within the federation's GP practices",
      'Lambeth Careers event week delivered with national attendance via targeted digital advertising',
      "Elevated brand and recruitment presence positioning Lambeth as a place to build a GP career"
    ],
    services: ['Video Production', 'Website Design & Development', 'Digital Advertising', 'Graphic Design', 'Event Promotion', 'Social Media Strategy', 'Photography'],
    videos: [
      { type: 'youtube', id: 'kHMDPuLuMjA' }
    ],
    sections: [
      {
        heading: 'Digital Advertising Campaign',
        images: [
          'https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/oih86eus_GENERIC%20SOCIAL%204.jpg',
          'https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/3czm57kn_GENERIC%20SOCIAL%205.jpg',
          'https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/lgzrbp1w_GENERIC%20SOCIAL%209.jpg',
          'https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/4q6ljrjz_SOCIAL%20DESIGNED%201.png',
          'https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/jggom97l_SOCIAL%20DESIGNED%202.png'
        ]
      },
      {
        heading: 'Printed Materials Design',
        images: [
          'https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/zlskg9gg_brochure%20mock.jpg',
          'https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/9hf3af9s_INFO%20PACK%20DESIGN.jpg',
          'https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/hmrzfnv6_flyer-mockup.jpg'
        ]
      },
      {
        heading: 'Billboard Advertising',
        images: ['https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/2v94xc93_billboard%20mock%20C2%20copy.jpg']
      },
      {
        heading: 'Website We Built',
        images: ['https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/fhps0g4u_lambethgpfederation.co.uk_%20%281%29.png']
      }
    ]
  },
  {
    slug: 'onward-homes-induction-films',
    title: 'Bringing Culture to Life: Induction Films & Onboarding',
    client: 'Onward Homes',
    sector: 'Social Housing',
    category: 'staff-engagement',
    categoryLabel: 'Staff Engagement',
    summary: "We created a suite of induction films and branded materials for Onward Homes, helping one of the North West's largest social housing providers bring their culture and values to life from day one.",
    hero: 'https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/69p0h0pi_DSCF8471.jpg',
    challenge: "As Onward Homes grew across the North West, their employee induction process wasn't keeping pace, and new starters were joining without a consistent, engaging introduction to the culture.\n\nOnboarding needed to reflect Onward's purpose meaningfully, not just tick a box.",
    solution: "We built a full induction programme combining films and designed print materials that brought Onward's purpose to life, showing real homes, real communities and real impact.\n\nWe also developed content for an internal onboarding system so every asset sits in one accessible place for new starters throughout their journey.",
    results: [
      'Positive qualitative feedback from new starters on the clarity and warmth of the induction experience',
      "Stronger cultural alignment from the outset, with employees arriving with a clear understanding of Onward's mission and values",
      'A scalable onboarding framework that can grow with the organisation as headcount increases across the region'
    ],
    services: ['Video Production', 'Graphic Design', 'Print Materials', 'Onboarding System Development', 'Photography'],
    videos: [
      { type: 'youtube', id: 'giYPbs9zJeQ' },
      { type: 'youtube', id: 'y2mJVjqy_iA' }
    ],
    gallery: [
      'https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/69p0h0pi_DSCF8471.jpg',
      'https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/5xrkyv66_DSCF0097.jpg',
      'https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/nwmxob7c_DSCF7428.jpg',
      'https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/mipkevb3_DSCF7658.jpg',
      'https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/48xv492y_DSCF8415.jpg'
    ]
  },
  {
    slug: 'birkenhead-sixth-form-recruitment',
    title: 'Attracting the Right Teachers Through Honest Recruitment',
    client: 'Birkenhead Sixth Form College',
    sector: 'Education',
    category: 'recruitment',
    categoryLabel: 'Recruitment',
    summary: "Birkenhead Sixth Form College is known for delivering high academic standards and supporting students to achieve ambitious outcomes. However, maintaining that standard requires teachers who not only meet the qualifications, but also share the college's drive, commitment, and long-term outlook. When the college needed to recruit new teachers, they wanted to ensure they were attracting the right candidates, not simply more candidates.",
    hero: 'https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/35nrtooj_IMG_4352.jpg',
    challenge: "Birkenhead Sixth Form College needed to attract motivated teachers aligned with their high standards, not just more applications.\n\nTraditional recruitment content often oversells the opportunity while under-explaining the reality of the role.",
    solution: "We produced a recruitment video, supporting marketing materials and a dedicated landing page built around honesty and clarity.\n\nAfter a deep dive with staff and analysis of wider education trends, the content balanced inspiration with realism, attracting teachers who wanted the challenge and naturally filtering out those who didn't.",
    results: [
      "Strong interest from high-quality candidates aligned with the college's culture and standards",
      'Successful recruitment across all advertised roles',
      'Greater confidence from leadership in the long-term fit of new hires',
      'Better alignment between the institution and applicants from the outset'
    ],
    services: ['Video Production', 'Graphic Design', 'Web Development', 'Recruitment Strategy', 'Photography'],
    videos: [
      { type: 'youtube', id: 'Oy5HnlvSU0o' },
      { type: 'youtube', id: 'bgFYWOSXwoA' }
    ],
    gallery: [
      'https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/tmcw92mc_IMG_3745.jpg',
      'https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/hup1loya_IMG_4318.jpg',
      'https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/35nrtooj_IMG_4352.jpg',
      'https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/i6ivb6o9_IMG_4397.jpg',
      'https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/1rnlgz9y_IMG_4833.jpg',
      'https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/d9dmod02_IMG_4423.jpg'
    ]
  },
  {
    slug: 'page-kirk-employer-brand',
    title: "Attracting Talent: Building Page Kirk's Employer Brand",
    client: 'Page Kirk',
    sector: 'Accountancy & Business Advisory',
    category: 'recruitment',
    categoryLabel: 'Recruitment',
    summary: "We created a new website alongside recruitment videos, photography and social media assets to help Page Kirk attract talented accountants and showcase the culture behind the firm.",
    hero: 'https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/0u5c9osy_IMG_1609.jpg',
    challenge: "Page Kirk faces stiff competition for talented graduates and experienced accountants, and standing out means more than listing vacancies.\n\nThey needed a modern digital presence and content that showed prospective recruits what the firm is really like to work for.",
    solution: "We designed and built a new website supported by recruitment videos, photography and social assets built to bring the culture to life.\n\nAlongside professional imagery, we photographed the team in relaxed poses to show that Page Kirk is both a highly professional firm and an enjoyable place to build a career.",
    results: [
      'Stronger employer brand helping Page Kirk attract the right talent',
      'Increased visibility of the culture behind the firm, not just the professional expertise',
      'Modern digital presence supporting both client trust and recruitment interest',
      'Improved quality of recruitment conversations, with candidates arriving with a real sense of the firm'
    ],
    services: ['Website Design & Development', 'Video Production', 'Photography', 'Recruitment Content Strategy', 'Social Media Assets'],
    videos: [
      { type: 'youtube', id: 'Reqbqk9rydk' },
      { type: 'youtube', id: 'sIvypbv_zPk' }
    ],
    sections: [
      {
        heading: 'Website We Built',
        images: ['https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/u0n22s51_Page%20Kirk%20recruit.JPG']
      },
      {
        heading: 'Photography from the Project',
        images: [
          'https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/0u5c9osy_IMG_1609.jpg',
          'https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/frg67u8f_IMG_1631.jpg',
          'https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/0wknmiir_IMG_2816.jpg',
          'https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/9ojvjq87_IMG_8872.jpg'
        ]
      }
    ]
  },
  {
    slug: 'momentum-group-onboarding-induction',
    title: 'Modernising Onboarding & Site Induction Through Video',
    client: 'The Momentum Group',
    sector: 'Construction & Property Management',
    category: 'staff-engagement',
    categoryLabel: 'Staff Engagement',
    summary: "The Momentum Group is built on a strong culture of professionalism, responsibility and collaboration. As the organisation continues to grow, ensuring every employee and contractor understands those values from the very beginning is essential. The leadership team recognised that first impressions matter, particularly when welcoming new starters or inducting contractors who will be working on live construction and managed sites.",
    hero: 'https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/rek6b5c6_IMG_6176_1999x1333.jpg',
    challenge: "Traditional site inductions rely on long presentations and documents that lead to information overload, low engagement and inconsistent delivery across sites.\n\nThe Momentum Group needed a way to communicate standards and culture efficiently to new starters, contractors and visitors alike.",
    solution: "We produced a welcome and induction video series covering three areas: culture and values, site safety expectations, and consistent delivery across every location.\n\nThe videos slot into existing induction programmes to break up longer sessions and keep attention high from the moment people arrive on-site.",
    results: [
      'More engaging induction days for new starters and contractors',
      'Consistent delivery of key safety and operational information across all sites',
      "Stronger reinforcement of the organisation's values and culture from day one",
      'Clear expectations for both employees and contractors entering the organisation'
    ],
    services: ['Video Production', 'Onboarding Strategy', 'Site Induction Content', 'Scriptwriting'],
    videos: [
      { type: 'youtube', id: 'ICf0K5rlhgU' },
      { type: 'youtube', id: '_R3tgBNBztY' }
    ],
    gallery: [
      'https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/7zwy2kgq_IMG_9237_1999x1333.jpg',
      'https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/cmlnons0_IMG_1784_1777x1333.JPG',
      'https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/m6e9l36u_IMG_5899_1999x1333.jpg',
      'https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/pll22939_IMG_9364_1999x1333.jpg',
      'https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/rek6b5c6_IMG_6176_1999x1333.jpg',
      'https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/8s3k8wuf_IMG_1787_1999x1333.jpg',
      'https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/556ry8qv_IMG_6083_1999x1333.jpg',
      'https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/okvu08h9_IMG_1841_1999x1333.jpg'
    ]
  },
  {
    slug: 'crown-estate-interactive-training',
    title: 'Interactive Health & Safety Training for Great Windsor Park',
    client: 'The Crown Estate',
    sector: 'Property & Land Management',
    category: 'training',
    categoryLabel: 'Training',
    summary: "Gateway Hub Consultants commissioned us as their content production partner to deliver a suite of video assets for an interactive health and safety training initiative developed for The Crown Estate's Grounds Team, working across Great Windsor Park.",
    hero: 'https://customer-assets.emergentagent.com/job_people-comms-hub/artifacts/3uacf1ah_IMG_0769.JPG',
    challenge: "The training wasn't a linear film but a branching, interactive experience: short scenarios, decision points and multiple filmed outcomes based on the user's choice.\n\nEvery action, reaction and consequence had to be planned, shot and structured to feel seamless, and this style of training was entirely new for the Grounds Team.",
    solution: "Working closely with Gateway, we translated the training logic into a clear, shootable structure and treated the production as a narrative system rather than a collection of clips.\n\nWe planned shoots to keep visual continuity across every branching scenario and captured performances that reflected day-to-day grounds work at Great Windsor Park.",
    results: [
      'Moved health and safety training from passive viewing to active decision-making',
      'Encouraged discussion and reflection within the Grounds Team',
      'Supported development of a stronger, more engaged working culture',
      'Demonstrated how interactive content can positively influence behaviour on the ground'
    ],
    services: ['Video Production', 'Script Development', 'Scenario Planning', 'On-location Filming', 'Interactive Content Design', 'Post-production'],
    videos: [
      { type: 'youtube', id: 'AnKGZ_M__70' }
    ],
    sections: [
      {
        heading: 'Behind the Scenes',
        images: [
          'https://customer-assets.emergentagent.com/job_people-comms-hub/artifacts/xozrfejp_IMG_0252.JPG',
          'https://customer-assets.emergentagent.com/job_people-comms-hub/artifacts/6lcza4bw_IMG_0549.JPG',
          'https://customer-assets.emergentagent.com/job_people-comms-hub/artifacts/srd56eat_IMG_0565.JPG',
          'https://customer-assets.emergentagent.com/job_people-comms-hub/artifacts/3uacf1ah_IMG_0769.JPG',
          'https://customer-assets.emergentagent.com/job_people-comms-hub/artifacts/urig0ysx_IMG_0893.JPG'
        ]
      }
    ]
  },
  {
    slug: 'bellrock-ongoing-communications',
    title: 'Ongoing Internal Communications Programme for Bellrock',
    client: 'Bellrock',
    sector: 'Facilities Management',
    category: 'staff-engagement',
    categoryLabel: 'Staff Engagement',
    summary: "We delivered an ongoing programme of video content, email campaigns and social posts for Bellrock, keeping employees informed, engaged and connected across their facilities management business.",
    hero: 'https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/xl3p58rw_image.png',
    challenge: "Bellrock needed a steady, reliable flow of internal communications to keep their workforce engaged, but producing that volume of quality content consistently was a challenge.\n\nWithout a continuous pipeline, internal messaging risked becoming sporadic and losing its impact.",
    solution: "We became Bellrock's dedicated comms partner, delivering a regular multi-channel programme of video, email campaigns, newsletters and social content.\n\nWhen Covid hit we adapted quickly, producing a virtual Awards Night that brought the whole company together at the moment connection mattered most.",
    results: [
      'Positive qualitative feedback from employees on the consistency and quality of internal communications',
      'A reliable content pipeline that removed the pressure of ad hoc production from the internal team',
      'Stronger employee connection to the business through regular, engaging multi-channel communications'
    ],
    services: ['Video Production', 'Email Campaigns', 'Newsletter Design', 'Social Media Content', 'Virtual Events', 'Content Strategy'],
    videos: [
      { type: 'youtube', id: 'RL855HOQ0hM' },
      { type: 'youtube', id: 'jgnubttZgqw' }
    ],
    sections: [
      {
        heading: 'Content Design',
        images: [
          'https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/thgv5nqi_Screenshot%202026-03-10%20164725.png',
          'https://customer-assets.emergentagent.com/job_23363ffb-eceb-44af-926f-252c6605d809/artifacts/zay0kv8l_Screenshot%202026-03-10%20164920.png'
        ]
      }
    ]
  }
];

export const getCaseStudy = (slug) => CASE_STUDIES.find((c) => c.slug === slug);

const SITE = 'https://yellowagency.co.uk';

export const caseStudySchema = (cs) => {
  const url = `${SITE}/case-studies/${cs.slug}`;
  const results = cs.results || [];
  return {
    '@context': 'https://schema.org',
    '@graph': [
      {
        '@type': 'CreativeWork',
        '@id': `${url}#casestudy`,
        name: cs.title,
        headline: cs.title,
        url,
        image: cs.hero,
        genre: 'Case study',
        abstract: cs.summary,
        description: cs.summary,
        text: [cs.challenge, cs.solution, results.length ? `Results: ${results.join('. ')}.` : ''].filter(Boolean).join('\n\n'),
        keywords: [cs.categoryLabel, cs.sector, ...(cs.services || [])].filter(Boolean).join(', '),
        about: (cs.services || []).map((s) => ({ '@type': 'Service', name: s, provider: { '@id': `${SITE}/#organization` } })),
        creator: { '@id': `${SITE}/#organization` },
        publisher: { '@id': `${SITE}/#organization` },
        sponsor: { '@type': 'Organization', name: cs.client, description: cs.sector },
        locationCreated: { '@type': 'Place', name: 'Liverpool, United Kingdom' },
        isPartOf: { '@type': 'CollectionPage', '@id': `${SITE}/case-studies`, name: 'Yellow Agency case studies' }
      },
      {
        '@type': 'BreadcrumbList',
        itemListElement: [
          { '@type': 'ListItem', position: 1, name: 'Home', item: `${SITE}/` },
          { '@type': 'ListItem', position: 2, name: 'Case Studies', item: `${SITE}/case-studies` },
          { '@type': 'ListItem', position: 3, name: cs.title, item: url }
        ]
      }
    ]
  };
};
